import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import { DIALOG_CLOSE_BUTTON_SX } from "../../constants/dialogCloseButtonStyles";
import { withoutBackdropClose } from "../../utils/withoutBackdropClose";
import {
  AUTO_TAGGING_CONFIRM_CANCEL_BUTTON_SX,
  AUTO_TAGGING_CONFIRM_CONFIRM_BUTTON_SX,
  AUTO_TAGGING_CONFIRM_DIALOG_BODY_SX,
  AUTO_TAGGING_CONFIRM_DIALOG_PAPER_SX,
  AUTO_TAGGING_CONFIRM_DIALOG_TITLE_SX,
} from "./autoTaggingConfirmDialogStyles";

export type AutoTaggingConfirmMode = "turnOn" | "turnOff";

type AutoTaggingConfirmDialogProps = Readonly<{
  open: boolean;
  mode: AutoTaggingConfirmMode | null;
  saving?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}>;

const DIALOG_COPY: Record<
  AutoTaggingConfirmMode,
  { title: string; body: string }
> = {
  turnOff: {
    title: "Turn off auto-tagging?",
    body: "Auto-tagging will stop running for new items. Existing tags and auto-tagging configurations will not be removed.",
  },
  turnOn: {
    title: "Turn on auto-tagging",
    body: "New items will be tagged automatically based on the configured auto-tagging rules. You can review and edit the generated tags after they are created.",
  },
};

export default function AutoTaggingConfirmDialog({
  open,
  mode,
  saving = false,
  onClose,
  onConfirm,
}: AutoTaggingConfirmDialogProps) {
  const copy = mode ? DIALOG_COPY[mode] : null;

  return (
    <Dialog
      open={open}
      onClose={withoutBackdropClose(onClose)}
      maxWidth={false}
      PaperProps={{ sx: AUTO_TAGGING_CONFIRM_DIALOG_PAPER_SX }}
    >
      <DialogTitle sx={AUTO_TAGGING_CONFIRM_DIALOG_TITLE_SX}>
        {copy?.title}
        <IconButton
          aria-label="Close"
          onClick={onClose}
          disabled={saving}
          sx={DIALOG_CLOSE_BUTTON_SX}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 0, pb: 1 }}>
        <Typography sx={AUTO_TAGGING_CONFIRM_DIALOG_BODY_SX}>
          {copy?.body}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          variant="text"
          onClick={onClose}
          disabled={saving}
          sx={AUTO_TAGGING_CONFIRM_CANCEL_BUTTON_SX}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          disabled={saving}
          sx={AUTO_TAGGING_CONFIRM_CONFIRM_BUTTON_SX}
        >
          {saving ? "Confirming…" : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
